// Client-side replica of the backend calculator logic

export function computePlan({
  monthlyExpenses,
  currentAge,
  freedomAge,
  annualReturn,
  annualInflation,
  stepupPct,
}) {
  const n = Math.max(1, freedomAge - currentAge);
  const r = annualReturn;
  const i = annualInflation;
  const g = stepupPct;

  const futureMonthlyExpense = monthlyExpenses * Math.pow(1 + i, n);
  const futureAnnualExpense = futureMonthlyExpense * 12;

  // Growing perpetuity for inflation-adjusted income
  const targetCorpus = futureAnnualExpense / (r - i);

  // Normal SIP (monthly ordinary annuity)
  const rm = Math.pow(1 + r, 1 / 12) - 1;
  const N = n * 12;
  const fvFactor = (Math.pow(1 + rm, N) - 1) / rm;
  const normalSip = targetCorpus / fvFactor;

  // Step-up SIP — yearly step
  const R = Math.pow(1 + rm, 12) - 1; // equals r
  const yearAnnuityFactor = (Math.pow(1 + rm, 12) - 1) / rm; // FV of 12 monthly ₹1 deposits
  let S;
  if (Math.abs(g - R) < 1e-9) {
    S = n * Math.pow(1 + R, n - 1);
  } else {
    S = (Math.pow(1 + g, n) - Math.pow(1 + R, n)) / (g - R);
  }
  const stepupSip = targetCorpus / (yearAnnuityFactor * S);

  // Year-by-year corpus trajectory
  const corpusGrowth = [];
  let balNormal = 0;
  let balStepup = 0;
  for (let year = 1; year <= n; year++) {
    balNormal = balNormal * (1 + R) + normalSip * yearAnnuityFactor;
    const thisYearMonthly = stepupSip * Math.pow(1 + g, year - 1);
    balStepup = balStepup * (1 + R) + thisYearMonthly * yearAnnuityFactor;
    corpusGrowth.push({
      year,
      age: currentAge + year,
      corpusNormal: Math.round(balNormal),
      corpusStepup: Math.round(balStepup),
      target: Math.round(targetCorpus),
    });
  }

  // Income roadmap — until age 80
  const incomeRoadmap = [];
  const yearsAfter = Math.max(1, 81 - freedomAge);
  for (let k = 0; k < yearsAfter; k++) {
    const age = freedomAge + k;
    const monthlyIncome = futureMonthlyExpense * Math.pow(1 + i, k);
    incomeRoadmap.push({
      age,
      monthlyIncome: Math.round(monthlyIncome),
      yearlyIncome: Math.round(monthlyIncome * 12),
    });
  }

  // Distribution period — corpus trajectory during withdrawal phase
  const distributionPeriod = [];
  for (let k = 0; k < yearsAfter; k++) {
    const age = freedomAge + k;
    const corpusNominal = targetCorpus * Math.pow(1 + i, k);
    const annualWithdrawal = futureAnnualExpense * Math.pow(1 + i, k);
    distributionPeriod.push({
      age,
      corpusNominal: Math.round(corpusNominal),
      corpusReal: Math.round(targetCorpus),
      annualWithdrawal: Math.round(annualWithdrawal),
    });
  }

  return {
    inputs: {
      monthlyExpenses,
      currentAge,
      freedomAge,
      annualReturn,
      annualInflation,
      stepupPct,
      yearsToFreedom: n,
    },
    futureMonthlyExpense: Math.round(futureMonthlyExpense),
    targetCorpus: Math.round(targetCorpus),
    normalSip: Math.round(normalSip),
    stepupSip: Math.round(stepupSip),
    corpusGrowth,
    incomeRoadmap,
    distributionPeriod,
  };
}
